import { useEffect, useState } from "react";
import axios from "axios";
import { AlertOctagon, Wrench, Trophy, Home, Building2 } from "lucide-react";
import { NARRATIVE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudies({ defaultTipo = "privato" }) {
  const [studies, setStudies] = useState([]);
  const [tipo, setTipo] = useState(defaultTipo);

  useEffect(() => {
    axios.get(`${API}/case-studies`).then(r => setStudies(r.data)).catch(() => {});
  }, []);

  const filtered = studies.filter(s => s.tipo === tipo);
  if (!studies.length) return null;

  return (
    <section id="casi-studio" data-testid={NARRATIVE.casiStudio} className="bg-gradient-to-b from-[#0a1f1a] via-[#061414] to-[#0a1f1a] text-white py-24 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#00D4FF]/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="overline text-emerald-400 mb-3">Scenari di analisi · Problema → Valutazione</div>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter">
              Ogni situazione richiede una valutazione diversa.
            </h2>
            <p className="mt-4 text-white/60 text-sm md:text-base leading-relaxed">
              Questi scenari sono esempi illustrativi, non clienti o interventi realmente eseguiti. Mostrano come esigenze diverse possano richiedere verifiche e soluzioni differenti. Prestazioni, costi e risultati effettivi dipendono dai dati del singolo caso e dalla verifica tecnica.
            </p>
          </div>

          <div className="inline-flex border border-white/20 rounded-md overflow-hidden">
            <button
              type="button" aria-pressed={tipo === "privato"}
              onClick={() => setTipo("privato")}
              className={`min-h-[44px] px-5 py-2.5 text-sm flex items-center gap-2 ${tipo === "privato" ? "bg-emerald-800 text-white" : "text-white/60 hover:text-white"}`}
            >
              <Home size={14} /> Privati
            </button>
            <button
              type="button" aria-pressed={tipo === "azienda"}
              onClick={() => setTipo("azienda")}
              className={`min-h-[44px] px-5 py-2.5 text-sm flex items-center gap-2 border-l border-white/20 ${tipo === "azienda" ? "bg-cyan-800 text-white" : "text-white/60 hover:text-white"}`}
            >
              <Building2 size={14} /> Aziende
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((cs, i) => (
            <article
              key={cs.id}
              data-testid={NARRATIVE.caseItem(cs.id)}
              className="border border-white/15 bg-white/[0.03] rounded-md overflow-hidden hover:border-emerald-400/40 transition-colors group flex flex-col"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              {cs.image_url && (
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={cs.image_url} alt={cs.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
                <div className="inline-flex self-start items-center gap-1.5 px-2 py-0.5 rounded border border-emerald-400/30 bg-emerald-500/10 text-emerald-300 text-[9px] uppercase tracking-widest mb-3">
                  Esempio illustrativo
                </div>
                <h3 className="font-display font-bold text-xl leading-tight mb-4 tracking-tight">
                  {cs.title}
                </h3>

                <div className="space-y-4 text-sm flex-1">
                  <div>
                    <div className="flex items-center gap-2 text-red-400 mb-1.5">
                      <AlertOctagon size={13} />
                      <span className="overline text-red-400">Problema</span>
                    </div>
                    <p className="text-white/70 leading-relaxed">{cs.problem}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-emerald-400 mb-1.5">
                      <Wrench size={13} />
                      <span className="overline text-emerald-400">Possibile configurazione</span>
                    </div>
                    <p className="text-white/70 leading-relaxed">{cs.solution}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-[#00D4FF] mb-1.5">
                      <Trophy size={13} />
                      <span className="overline text-[#00D4FF]">Obiettivo della valutazione</span>
                    </div>
                    <p className="text-white/70 leading-relaxed">{cs.outcome}</p>
                  </div>
                </div>

                {cs.tags && cs.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-5 pt-5 border-t border-white/10">
                    {cs.tags.map((t, k) => (
                      <span key={k} className="px-2 py-1 rounded bg-white/5 border border-white/10 text-[10px] uppercase tracking-wider text-white/60">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
